import React, { useState, useEffect } from "react";
import { Field, formValueSelector, getFormSubmitErrors, getFormSyncErrors, reduxForm, SubmissionError } from "redux-form";
import { Button, Form } from "@dpdgroupuk/mydpd-ui";
import PropTypes from "prop-types";
import clsx from "clsx";
import { connect } from "react-redux";
import { isEmpty } from "lodash";
import { withTrackProps } from "@dpdgroupuk/react-event-tracker";
import { compose } from "recompose";
import PasswordStrengthMeter from "../../../../components/PasswordStrengthMeter";
import { FormUtil, AuthUtil } from "../../../../utils";
import FormError from "../../../../components/FormError";
import { AUTH, ANALYTICS, FORM } from "../../../../constants";
import styles from "./NewPasswordForm.module.scss";

const NewPasswordForm = ({
  error,
  handleSubmit,
  pristine,
  submitting,
  enabled,
  onCancel,
  newPassword,
  newPasswordConfirmation,
  oldPassword,
  onSubmit,
  onSaveNewPassword,
  newPasswordError,
  onInputChange,
  onNewPasswordToggle,
  onNewPasswordConfirmationToggle
}) => {
  const [newPasswordStrength, setNewPasswordStrength] = useState(0);
  useEffect(async () => {
    let passwordStrength = 0;

    if (newPassword) {
      passwordStrength = await AuthUtil.passwordStrength(newPassword || "");
    }

    setNewPasswordStrength(passwordStrength);
  }, [newPassword]);
  const showPasswordMeter = !newPasswordError && !isEmpty(newPassword);

  const validateForm = formValues => {
    if (newPasswordStrength < AUTH.PASSWORD_STRENGTH_SCORE.GOOD_PASSWORD) {
      throw new SubmissionError({
        newPassword: "New Password is too weak"
      });
    }

    if (newPassword !== newPasswordConfirmation) {
      throw new SubmissionError({
        newPasswordConfirmation: "Passwords do not match"
      });
    }

    if (oldPassword === newPassword) {
      throw new SubmissionError({
        newPassword: "Sorry, the new password cannot be the same as the old one"
      });
    }

    return onSubmit(formValues);
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("h2", {
    className: styles.header
  }, "Create your new password"), /*#__PURE__*/React.createElement("form", {
    onSubmit: handleSubmit(validateForm),
    autoComplete: "off"
  }, /*#__PURE__*/React.createElement(Field, {
    name: "newPassword",
    id: "newPasswordField",
    type: "password",
    label: "Enter New Password",
    component: Form.MaterialTextInput,
    maxLength: 100,
    disabled: !enabled,
    validate: [FormUtil.validatePasswordLength],
    helperText: " " // this allows error to be shown
    ,
    onChange: onInputChange,
    onPasswordVisibilityToggle: onNewPasswordToggle
  }), showPasswordMeter && /*#__PURE__*/React.createElement("div", {
    className: styles.strengthMeter
  }, /*#__PURE__*/React.createElement(PasswordStrengthMeter, {
    passwordScore: newPasswordStrength
  })), /*#__PURE__*/React.createElement(Field, {
    name: "newPasswordConfirmation",
    id: "newPasswordConfirmationField",
    type: "password",
    label: "Confirm New Password",
    component: Form.MaterialTextInput,
    maxLength: 100,
    disabled: !enabled,
    helperText: " " // this allows error to be shown
    ,
    onChange: onInputChange,
    onPasswordVisibilityToggle: onNewPasswordConfirmationToggle
  }), /*#__PURE__*/React.createElement("div", {
    className: clsx(styles.buttons, {
      [styles.hidden]: !enabled
    })
  }, /*#__PURE__*/React.createElement(Button, {
    id: "cancelBtn",
    onClick: onCancel,
    variant: "dark",
    className: styles.button
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    id: "submitBtn",
    type: "submit",
    variant: "primary",
    disabled: newPasswordStrength < AUTH.PASSWORD_STRENGTH_SCORE.GOOD_PASSWORD || pristine || submitting || isEmpty(newPassword) || isEmpty(newPasswordConfirmation),
    className: styles.button,
    onClick: onSaveNewPassword
  }, "Save New Password"))), enabled && /*#__PURE__*/React.createElement("div", {
    className: styles.error
  }, /*#__PURE__*/React.createElement(FormError, {
    errorMessage: error
  })));
};

NewPasswordForm.propTypes = {
  error: PropTypes.string,
  handleSubmit: PropTypes.func,
  pristine: PropTypes.bool,
  submitting: PropTypes.bool,
  enabled: PropTypes.bool,
  onCancel: PropTypes.func,
  newPassword: PropTypes.string,
  newPasswordConfirmation: PropTypes.string,
  oldPassword: PropTypes.string,
  onSubmit: PropTypes.func,
  onSaveNewPassword: PropTypes.func,
  newPasswordError: PropTypes.bool,
  onInputChange: PropTypes.func,
  onNewPasswordToggle: PropTypes.func,
  onNewPasswordConfirmationToggle: PropTypes.func
};
const selector = formValueSelector(FORM.UPDATE_PASSWORD_FORM);
export default compose(connect(state => {
  const newPassword = selector(state, "newPassword");
  const newPasswordConfirmation = selector(state, "newPasswordConfirmation");
  const oldPassword = selector(state, "password");
  const newPasswordError = !isEmpty(getFormSyncErrors(FORM.UPDATE_PASSWORD_FORM)(state)?.newPassword) || !isEmpty(getFormSubmitErrors(FORM.UPDATE_PASSWORD_FORM)(state)?.newPassword);
  return {
    newPassword,
    newPasswordConfirmation,
    oldPassword,
    newPasswordError
  };
}), reduxForm({
  form: FORM.UPDATE_PASSWORD_FORM,
  validate: fields => {
    const errors = {};

    if (fields.newPassword && fields.newPasswordConfirmation !== fields.newPassword) {
      errors.newPasswordConfirmation = "Passwords do not match";
    }

    return errors;
  }
}), withTrackProps({
  onCancel: ANALYTICS.UPDATE_PASSWORD_PAGE.ON_CANCEL,
  onSaveNewPassword: ANALYTICS.UPDATE_PASSWORD_PAGE.ON_SAVE_NEW_PASSWORD,
  onInputChange: (e, prevValues, nextValues, name) => ANALYTICS.UPDATE_PASSWORD_PAGE.ON_INPUT_CHANGE[name],
  onNewPasswordToggle: ANALYTICS.UPDATE_PASSWORD_PAGE.ON_NEW_PASSWORD_TOGGLE,
  onNewPasswordConfirmationToggle: ANALYTICS.UPDATE_PASSWORD_PAGE.ON_CONFIRM_NEW_PASSWORD_TOGGLE
}))(NewPasswordForm);